import { useMemo } from "react";
import {
  COLOR_KEYS,
  AREA_COLORS,
  getListColor,
  resolvePairedColorKeys,
  type ListColorKey,
} from "@/lib/listColors";
import { Check } from "lucide-react";

interface SwatchProps {
  colorKey: ListColorKey;
  selected: boolean;
  onSelect: () => void;
  variant: "bar" | "soft";
}

function Swatch({ colorKey, selected, onSelect, variant }: SwatchProps) {
  const c = AREA_COLORS[colorKey];
  const bg = variant === "bar" ? c.base : c.soft;
  const ringColor = variant === "bar" ? c.ink : c.base;
  return (
    <button
      type="button"
      onClick={onSelect}
      title={c.name}
      aria-label={`${c.name}${variant === "soft" ? " (secondary)" : ""}`}
      className="ui-color-picker__swatch relative h-8 w-8 rounded-full transition-all hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      style={{
        background: bg,
        boxShadow: selected
          ? `0 0 0 2px var(--background), 0 0 0 4px ${ringColor}`
          : "0 0 0 1px rgba(0,0,0,0.08)",
      }}
    >
      {selected && (
        <Check
          className="absolute inset-0 m-auto h-3.5 w-3.5"
          style={{ color: variant === "bar" ? "white" : c.ink }}
          strokeWidth={3}
        />
      )}
    </button>
  );
}

interface ColorSwatchGridProps {
  value: string | null | undefined;
  onChange: (key: ListColorKey | null) => void;
  variant: "bar" | "soft";
  auto?: {
    label: string;
    ariaLabel: string;
    title: string;
    color: string | null | undefined;
  };
}

export function ColorSwatchGrid({
  value,
  onChange,
  variant,
  auto,
}: ColorSwatchGridProps) {
  const current = useMemo(
    () => (value && (COLOR_KEYS as string[]).includes(value) ? (value as ListColorKey) : null),
    [value],
  );
  const autoColor = getListColor(auto?.color);
  return (
    <div className="ui-color-picker__swatch-grid flex flex-wrap items-center gap-1.5">
      {auto && (
        <button
          type="button"
          onClick={() => onChange(null)}
          aria-label={auto.ariaLabel}
          title={auto.title}
          className="ui-color-picker__auto relative h-8 px-2.5 rounded-full text-[11px] font-medium uppercase tracking-wider transition-all"
          style={{
            background: variant === "bar" ? autoColor.base : autoColor.soft,
            color: variant === "bar" ? "white" : autoColor.ink,
            boxShadow:
              current === null
                ? `0 0 0 2px var(--background), 0 0 0 4px ${
                    variant === "bar" ? autoColor.ink : autoColor.base
                  }`
                : "0 0 0 1px rgba(0,0,0,0.08)",
          }}
        >
          {auto.label}
        </button>
      )}
      {COLOR_KEYS.map(k => (
        <Swatch
          key={k}
          colorKey={k}
          selected={current === k}
          onSelect={() => onChange(k)}
          variant={variant}
        />
      ))}
    </div>
  );
}

type AutoColorSwatchGridProps = Omit<ColorSwatchGridProps, "auto"> & {
  auto: NonNullable<ColorSwatchGridProps["auto"]>;
};

export function AutoColorSwatchGrid(props: AutoColorSwatchGridProps) {
  return (
    <div className="ui-color-picker ui-color-picker--auto">
      <ColorSwatchGrid {...props} />
    </div>
  );
}

interface BgPickerProps {
  /** Currently selected background color key (or null/undefined = unset). */
  value: string | null | undefined;
  /**
   * Called whenever the user picks a background. Pass `null` to clear.
   * Background is the PRIMARY color; the bar follows it unless explicitly overridden.
   */
  onChange: (key: ListColorKey | null) => void;
  label?: string;
  /** Whether to show a "Clear" / null option (for instance/template overrides). */
  allowClear?: boolean;
  /** Whether to show an Auto swatch that clears the override. */
  showAuto?: boolean;
  /** Effective color used to render the Auto swatch. */
  autoColor?: string | null | undefined;
}

/**
 * Background tint picker — this is the PRIMARY color choice.
 * Picking a background should also reset the bar to match (handled by the caller).
 */
export function BackgroundColorPicker({
  value,
  onChange,
  label = "Secondary color",
  allowClear = false,
  showAuto = false,
  autoColor,
}: BgPickerProps) {
  const current = useMemo(
    () => (value && (COLOR_KEYS as string[]).includes(value) ? (value as ListColorKey) : null),
    [value],
  );
  return (
    <div className="ui-color-picker ui-color-picker--background space-y-2">
      {(label || (allowClear && current !== null)) && (
        <div className="ui-color-picker__header flex items-baseline justify-between">
          {label && (
            <p className="ui-color-picker__label text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          )}
          {allowClear && current !== null && (
            <button
              type="button"
              className="ui-color-picker__clear text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              onClick={() => onChange(null)}
            >
              Clear
            </button>
          )}
        </div>
      )}
      {showAuto ? (
        <AutoColorSwatchGrid
          value={value}
          onChange={onChange}
          variant="soft"
          auto={{
            label: "Auto",
            ariaLabel: "Auto secondary",
            title: "Auto (matches primary)",
            color: autoColor,
          }}
        />
      ) : (
        <ColorSwatchGrid value={value} onChange={onChange} variant="soft" />
      )}
    </div>
  );
}

interface BarOverrideProps {
  /** Currently selected bar color key (or null = follow secondary). */
  value: string | null | undefined;
  /** Called when the user picks a bar color. Pass `null` to defer back to secondary. */
  onChange: (key: ListColorKey | null) => void;
  /** The currently effective secondary color, used as the "Auto" default for primary. */
  bgColor: string | null | undefined;
  label?: string;
}

/**
 * Optional bar (sidebar) color override picker.
 * The primary defaults to follow secondary; this lets the user pick a different one.
 */
export function BarColorOverridePicker({
  value,
  onChange,
  bgColor,
  label = "Primary color",
}: BarOverrideProps) {
  return (
    <div className="ui-color-picker ui-color-picker--primary space-y-2">
      {label && (
        <div className="ui-color-picker__header flex items-baseline justify-between">
          <p className="ui-color-picker__label text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
        </div>
      )}
      <AutoColorSwatchGrid
        value={value}
        onChange={onChange}
        variant="bar"
        auto={{
          label: "Auto",
          ariaLabel: "Auto primary",
          title: "Auto (matches secondary)",
          color: bgColor,
        }}
      />
    </div>
  );
}

interface PreviewProps {
  barColor: string | null | undefined;
  bgColor: string | null | undefined;
  /** Inherited values used when explicit values are null (template falls back to list, etc.). */
  fallbackBar?: string | null;
  fallbackBg?: string | null;
  title?: string;
  label?: string;
}

/**
 * Live preview of a task card with the chosen colors.
 * Background is primary; bar defaults to bg when not explicitly set.
 */
export function ColorPreviewCard({
  barColor,
  bgColor,
  fallbackBar,
  fallbackBg,
  title = "Sample task",
  label = "List",
}: PreviewProps) {
  const resolved = resolvePairedColorKeys({
    primaryColor: barColor,
    secondaryColor: bgColor,
    fallbackPrimary: fallbackBar,
    fallbackSecondary: fallbackBg,
  });
  const bg = getListColor(resolved.secondary);
  const bar = getListColor(resolved.primary);
  return (
    <div
      className="ui-color-preview-card rounded-xl border border-border/40 overflow-hidden flex"
      style={{ background: bg.soft }}
    >
      <div className="ui-color-preview-card__bar w-1.5 shrink-0" style={{ background: bar.base }} />
      <div className="ui-color-preview-card__content px-4 py-3 flex-1">
        <div
          className="ui-color-preview-card__label text-[10px] uppercase tracking-wider font-medium mb-0.5"
          style={{ color: bar.ink }}
        >
          {label}
        </div>
        <div className="ui-color-preview-card__title font-display text-base">{title}</div>
        <div className="ui-color-preview-card__meta text-xs text-muted-foreground mt-0.5">06:00 · 60 min repeat</div>
      </div>
    </div>
  );
}

/** Returns true when the value is a recognized palette key. */
export function isPaletteKey(value: unknown): value is ListColorKey {
  return typeof value === "string" && (COLOR_KEYS as string[]).includes(value);
}

// ---- Backwards-compat aliases (legacy import names) ----
// MainColorPicker (the bar-only single picker) is replaced by BackgroundColorPicker.
// BgColorOverridePicker (background override deferring to bar) becomes
// BarColorOverridePicker (bar override deferring to bg). These aliases keep
// any pages from breaking during the rewire; new code should use the new names.
export { BackgroundColorPicker as MainColorPicker };
export { BarColorOverridePicker as BgColorOverridePicker };
